import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { CONTACT, SERVICES } from "../data";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_OPTIONS = [...SERVICES.map((s) => s.title), "Other / Not sure"];

export const QuoteForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: SERVICE_OPTIONS[0], address: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      setDone(true);
      toast.success("Quote request sent! Kyle will be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b-2 border-stone focus:border-terra outline-none py-3 text-pine placeholder:text-pine/40 transition-colors";
  const labelC = "text-xs uppercase tracking-[0.2em] text-pine/50 mb-1 block";

  return (
    <section id="quote" data-testid="quote" className="bg-bone py-24 md:py-32 border-t border-stone">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-[1px] w-10 bg-terra" />
            <span className="text-pine/60 text-xs uppercase tracking-[0.3em]">Get started</span>
          </div>
          <Reveal>
            <h2 className="font-display font-extrabold text-pine tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.92]">
              Request a<br />free quote.
            </h2>
          </Reveal>
          <p className="mt-6 text-pine/70 leading-relaxed max-w-md">
            Tell us about the job and we'll get back to you with an honest, fair estimate. No pressure, no games.
          </p>

          <div className="mt-10 space-y-5">
            <a href={CONTACT.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="w-11 h-11 flex items-center justify-center bg-pine text-bone group-hover:bg-terra transition-colors"><Phone size={18} /></span>
              <div>
                <span className="block text-xs uppercase tracking-widest text-pine/50">Call / Text</span>
                <span className="font-semibold text-pine">{CONTACT.phone}</span>
              </div>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="w-11 h-11 flex items-center justify-center bg-pine text-bone group-hover:bg-terra transition-colors"><Mail size={18} /></span>
              <div>
                <span className="block text-xs uppercase tracking-widest text-pine/50">Email</span>
                <span className="font-semibold text-pine break-all">{CONTACT.email}</span>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 flex items-center justify-center bg-pine text-bone"><MapPin size={18} /></span>
              <div>
                <span className="block text-xs uppercase tracking-widest text-pine/50">Service Area</span>
                <span className="font-semibold text-pine">{CONTACT.areas}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="quote-success" className="border border-stone bg-pine text-bone p-10 md:p-14 h-full flex flex-col justify-center items-start">
              <CheckCircle2 className="text-terra mb-6" size={48} />
              <h3 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Request received.</h3>
              <p className="mt-4 text-bone/70 max-w-md leading-relaxed">
                Thanks, {form.name.split(" ")[0]}. Your request is in — {CONTACT.owner} will reach out shortly.
                Need us sooner? Call {CONTACT.phone}.
              </p>
            </div>
          ) : (
            <form data-testid="quote-form" onSubmit={submit} className="border border-stone p-7 md:p-10 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelC}>Name *</label>
                  <input data-testid="quote-name" className={field} value={form.name} onChange={set("name")} placeholder="Your name" />
                </div>
                <div>
                  <label className={labelC}>Phone *</label>
                  <input data-testid="quote-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" />
                </div>
                <div>
                  <label className={labelC}>Email *</label>
                  <input data-testid="quote-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                </div>
                <div>
                  <label className={labelC}>Service</label>
                  <select data-testid="quote-service" className={`${field} cursor-pointer`} value={form.service} onChange={set("service")}>
                    {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={labelC}>Property Address</label>
                  <input data-testid="quote-address" className={field} value={form.address} onChange={set("address")} placeholder="Street, city, county" />
                </div>
                <div className="md:col-span-2">
                  <label className={labelC}>Job Details</label>
                  <textarea data-testid="quote-message" rows={4} className={`${field} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about the trees or work needed…" />
                </div>
              </div>
              <button
                data-testid="quote-submit"
                type="submit"
                disabled={loading}
                className="btn-wipe mt-8 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-terra text-bone font-bold px-10 py-4 tracking-tight disabled:opacity-70"
              >
                {loading ? <><Loader2 className="animate-spin" size={18} /> Sending…</> : "Send Request"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
